import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { apiFetch } from '../../utils/api';
import { Info } from 'lucide-react';
import './ShippingSetupWizard.css';

// Default zones template (editable)
const defaultZones = [
  {
    name: 'Lagos Zone',
    states: ['Lagos'],
    locations: [],
    shippingCost: 1000,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 1
  },
  {
    name: 'South West Zone',
    states: ['Ogun', 'Ondo', 'Osun', 'Oyo', 'Ekiti'],
    locations: [],
    shippingCost: 1500,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 2
  },
  {
    name: 'South East Zone',
    states: ['Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo'],
    locations: [],
    shippingCost: 2000,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 3
  },
  {
    name: 'South South Zone',
    states: ['Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers'],
    locations: [],
    shippingCost: 2000,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 3
  },
  {
    name: 'North Central Zone',
    states: ['FCT', 'Benue', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau'],
    locations: [],
    shippingCost: 2500,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 4
  },
  {
    name: 'North West Zone',
    states: ['Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Jigawa', 'Zamfara'],
    locations: [],
    shippingCost: 3000,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 5
  },
  {
    name: 'North East Zone',
    states: ['Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe'],
    locations: [],
    shippingCost: 3000,
    freeShippingThreshold: 50000,
    estimatedDeliveryDays: 5
  }
];

const ShippingSetupWizard = ({ isOpen, onClose, onComplete, token, existingZones = [] }) => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    name: '',
    shippingCost: '',
    freeShippingThreshold: '',
    estimatedDeliveryDays: '3',
    description: '',
  });
  const [selectedStates, setSelectedStates] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const [locationRates, setLocationRates] = useState([]);

  // Load Nigerian states
  const loadStates = async () => {
    try {
      const response = await apiFetch('/shipping-zones/states');
      const data = await response.json();
      setAllStates(data.states || []);
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // If there are existing zones, start with them, otherwise use default zones
      if (existingZones.length > 0) {
        setZones([...existingZones]);
      } else {
        setZones([...defaultZones]); // Start with editable default zones
      }
      loadStates();
    }
  }, [isOpen, existingZones]);

  const parseList = (value) => String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  const normalizeLocationRates = (zone) => {
    const baseShipping = Number(zone?.shippingCost || 0);
    const raw = Array.isArray(zone?.locationRates) ? zone.locationRates : [];
    if (raw.length > 0) {
      return raw
        .map((item) => ({
          name: String(item?.name || item?.location || '').trim(),
          shippingCost: String(item?.shippingCost ?? baseShipping),
        }))
        .filter((item) => item.name);
    }

    return parseList(zone?.locations).map((name) => ({
      name,
      shippingCost: String(baseShipping),
    }));
  };

  const syncLocationRatesFromInput = () => {
    const names = parseList(locationInput);
    if (names.length === 0) return;

    setLocationRates((prev) => {
      const existing = new Set(prev.map((item) => item.name.toLowerCase()));
      const fallbackCost = zoneForm.shippingCost || '0';
      const additions = names
        .filter((name) => !existing.has(name.toLowerCase()))
        .map((name) => ({ name, shippingCost: String(fallbackCost) }));
      return [...prev, ...additions];
    });
    setLocationInput('');
  };

  const updateLocationRate = (index, field, value) => {
    setLocationRates((prev) => prev.map((item, idx) => (
      idx === index ? { ...item, [field]: value } : item
    )));
  };

  const removeLocationRate = (index) => {
    setLocationRates((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Handle zone editing
  const openEditZone = (zone, index) => {
    setEditingZone({ ...zone, index });
    setZoneForm({
      name: zone.name,
      shippingCost: zone.shippingCost.toString(),
      freeShippingThreshold: zone.freeShippingThreshold.toString(),
      estimatedDeliveryDays: zone.estimatedDeliveryDays.toString(),
      description: zone.description || '',
    });
    setSelectedStates([...zone.states]);
    setLocationInput('');
    setLocationRates(normalizeLocationRates(zone));
  };

  const closeEditZone = () => {
    setEditingZone(null);
    setZoneForm({
      name: '',
      shippingCost: '',
      freeShippingThreshold: '',
      estimatedDeliveryDays: '3',
      description: '',
    });
    setSelectedStates([]);
    setLocationInput('');
    setLocationRates([]);
  };

  const saveZoneEdit = async () => {
    if (!editingZone) return;

    const updatedZones = [...zones];
      const updatedZone = {
      ...updatedZones[editingZone.index],
      name: zoneForm.name,
      shippingCost: parseFloat(zoneForm.shippingCost) || 0,
      freeShippingThreshold: parseFloat(zoneForm.freeShippingThreshold) || 0,
      estimatedDeliveryDays: parseInt(zoneForm.estimatedDeliveryDays) || 3,
      description: zoneForm.description,
      states: selectedStates,
      locations: locationRates.map((item) => item.name),
      locationRates: locationRates.map((item) => ({
        name: item.name,
        shippingCost: Number(item.shippingCost) || Number(zoneForm.shippingCost) || 0,
      })),
    };

    console.log('Saving zone edit:', {
      index: editingZone.index,
      originalZone: updatedZones[editingZone.index],
      updatedZone: updatedZone,
      zoneForm: zoneForm,
      selectedStates: selectedStates
    });

    // If it's an existing zone (has _id), update it immediately in the database
    if (updatedZone._id) {
      try {
        console.log('Updating zone immediately in database:', updatedZone._id);
        const response = await apiFetch(`/shipping-zones/${updatedZone._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: updatedZone.name,
            states: updatedZone.states,
            locations: updatedZone.locations,
            shippingCost: updatedZone.shippingCost,
            freeShippingThreshold: updatedZone.freeShippingThreshold,
            estimatedDeliveryDays: updatedZone.estimatedDeliveryDays,
            description: updatedZone.description
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update zone in database');
        }

        console.log('Zone updated successfully in database');
      } catch (error) {
        console.error('Error updating zone in database:', error);
        setError('Failed to update zone: ' + error.message);
        return;
      }
    } else {
      // If it's a new zone (no _id), only create it if it has states selected
      if (updatedZone.states && updatedZone.states.length > 0) {
        try {
          console.log('Creating new zone immediately in database:', updatedZone);
          console.log('Zone states:', updatedZone.states);
          console.log('Zone states length:', updatedZone.states?.length);
          console.log('Zone name:', updatedZone.name);
          console.log('Zone shippingCost:', updatedZone.shippingCost);

          const requestBody = { zones: [updatedZone] };
          console.log('Request body being sent:', JSON.stringify(requestBody, null, 2));

          const response = await apiFetch('/shipping-zones/setup-custom', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });

        if (!response.ok) {
          let errorMessage = 'Failed to create zone in database';
          try {
            const errorData = await response.json();
            console.error('Create zone error data:', errorData);
            errorMessage = errorData.error || errorMessage;
          } catch (jsonError) {
            try {
              const errorText = await response.text();
              console.error('Create zone error text:', errorText);
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
          }
          throw new Error(errorMessage);
        }

          const createData = await response.json();
          console.log('Zone created successfully in database:', createData);

          // Update the zone with the _id from the database response
          if (createData.zones && createData.zones.length > 0) {
            updatedZone._id = createData.zones[0]._id;
          }
        } catch (error) {
          console.error('Error creating zone in database:', error);
          setError('Failed to create zone: ' + error.message);
          return;
        }
      } else {
        console.log('New zone has no states selected, will be created when states are added');
      }
    }

    updatedZones[editingZone.index] = updatedZone;
    setZones(updatedZones);
    closeEditZone();
  };

  const addNewZone = () => {
    const newZone = {
      name: 'New Zone',
      states: [],
      locations: [],
      shippingCost: 2000,
      freeShippingThreshold: 50000,
      estimatedDeliveryDays: 3,
      description: ''
    };
    setZones([...zones, newZone]);
    openEditZone(newZone, zones.length);
  };

  const removeZone = async (index) => {
    const zoneToRemove = zones[index];

    // If it's an existing zone (has _id), delete it immediately from the database
    if (zoneToRemove._id) {
      try {
        console.log('Deleting zone immediately from database:', zoneToRemove._id);
        const response = await apiFetch(`/shipping-zones/${zoneToRemove._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete zone from database');
        }

        console.log('Zone deleted successfully from database');
      } catch (error) {
        console.error('Error deleting zone from database:', error);
        setError('Failed to delete zone: ' + error.message);
        return;
      }
    }

    // Remove from local state
    const updatedZones = zones.filter((_, i) => i !== index);
    setZones(updatedZones);
  };

  const createZones = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only handle zones that still don't have an _id and have states selected
      const newZones = zones.filter(zone => !zone._id && zone.states && zone.states.length > 0);

      console.log('Remaining new zones to create:', newZones);

      // Create new zones (if any)
      if (newZones.length > 0) {
        console.log('Creating new zones:', newZones);

        const createResponse = await apiFetch('/shipping-zones/setup-custom', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ zones: newZones })
        });

        console.log('Create response status:', createResponse.status);

        if (!createResponse.ok) {
          let errorMessage = 'Failed to create new zones';
          try {
            const errorData = await createResponse.json();
            console.error('Create error data:', errorData);
            errorMessage = errorData.error || errorMessage;
          } catch (jsonError) {
            try {
              const errorText = await createResponse.text();
              console.error('Create error text:', errorText);
              errorMessage = errorText || errorMessage;
            } catch (textError) {
              errorMessage = `HTTP ${createResponse.status}: ${createResponse.statusText}`;
            }
          }
          throw new Error(errorMessage);
        }

        const createData = await createResponse.json();
        console.log('Zones created successfully:', createData);
      } else {
        console.log('No new zones to create');
      }

      console.log('All zones operations completed successfully');
      onComplete(); // Let the parent component refresh the zones list
      onClose();
    } catch (err) {
      console.error('Error updating/creating zones:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStateToggle = (state) => {
    if (selectedStates.includes(state)) {
      setSelectedStates(selectedStates.filter(s => s !== state));
    } else {
      setSelectedStates([...selectedStates, state]);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existingZones.length > 0 ? "Add More Shipping Zones" : "Setup Shipping Zones"}
      size="lg"
      padded={false}
      footer={
        <>
          <button className="btn btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-create"
            onClick={createZones}
            disabled={loading || zones.length === 0}
          >
            {loading ? 'Creating...' : `Create ${zones.length} ${zones.length === 1 ? 'Zone' : 'Zones'}`}
          </button>
        </>
      }
    >
      <div className="wizard-container">
        {error && (
          <div className="wizard-error">
            {error}
          </div>
        )}

        <div>
          <div className="wizard-header">
            <h3>
              {existingZones.length > 0 ? 'Add More Shipping Zones' : 'Customize Your Shipping Zones'}
            </h3>
            <p>
              {existingZones.length > 0
                ? 'Add additional shipping zones to expand your delivery coverage. You can also edit existing zones below.'
                : 'We\'ve prepared some default zones for you. Edit the costs, delivery times, and state groupings to match your business needs.'
              }
            </p>
          </div>

          <div className="wizard-zones-list">
            {zones.map((zone, index) => (
              <Card key={index}>
                <div className="wizard-zone-card">
                  <div className="wizard-zone-header">
                    <div className="wizard-zone-info">
                      <h4>{zone.name}</h4>
                      <div className="wizard-zone-meta">
                        <span>₦{zone.shippingCost.toLocaleString()}</span>
                        <span>{zone.estimatedDeliveryDays} days</span>
                        <span>{zone.states.length} states</span>
                        <span>{(zone.locationRates || zone.locations || []).length} locations</span>
                      </div>
                    </div>
                    <div className="wizard-zone-actions">
                      <button
                        className="btn-edit"
                        onClick={() => openEditZone(zone, index)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-remove"
                        onClick={() => removeZone(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="wizard-zone-states">
                    {zone.states.map((state, stateIndex) => (
                      <Badge key={stateIndex} color="gray">
                        {state}
                      </Badge>
                    ))}
                  </div>

                  {(zone.locationRates || zone.locations || []).length > 0 && (
                    <div className="wizard-zone-states">
                      {(Array.isArray(zone.locationRates) && zone.locationRates.length > 0
                        ? zone.locationRates
                        : zone.locations.map((location) => ({ name: location, shippingCost: zone.shippingCost }))
                      ).map((location, locationIndex) => (
                        <Badge key={locationIndex} color="blue">
                          {location.name} · ₦{Number(location.shippingCost || zone.shippingCost).toLocaleString()}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          <div className="wizard-add-row">
            <button className="btn btn-add" onClick={addNewZone}>
              + Add custom zone
            </button>
          </div>
        </div>

        {/* Edit Zone Modal */}
        {editingZone && (
          <Modal
            isOpen={!!editingZone}
            onClose={closeEditZone}
            title="Edit Zone"
            padded={false}
            footer={
              <>
                <button className="btn btn-cancel" onClick={closeEditZone}>
                  Cancel
                </button>
                <button className="btn btn-save" onClick={saveZoneEdit}>
                  Save Changes
                </button>
              </>
            }
          >
            <div className="edit-zone-content">
              <div className="edit-zone-form">
                <div className="edit-zone-form-group">
                  <label>Zone Name</label>
                  <Input
                    value={zoneForm.name}
                    onChange={(e) => setZoneForm({...zoneForm, name: e.target.value})}
                    placeholder="Enter zone name"
                  />
                </div>

                <div className="edit-zone-form-row">
                  <div className="edit-zone-form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Shipping Cost (₦)
                      <span
                        title="Required. Used as the fallback shipping cost when the customer’s street, city, town, or postcode does not match a location override."
                        style={{ display: 'inline-flex', alignItems: 'center', color: '#6b7280', cursor: 'help' }}
                      >
                        <Info size={13} />
                      </span>
                    </label>
                    <Input
                      type="number"
                      value={zoneForm.shippingCost}
                      onChange={(e) => setZoneForm({...zoneForm, shippingCost: e.target.value})}
                      placeholder="2000"
                    />
                  </div>

                  <div className="edit-zone-form-group">
                    <label>Free Shipping Threshold (₦)</label>
                    <Input
                      type="number"
                      value={zoneForm.freeShippingThreshold}
                      onChange={(e) => setZoneForm({...zoneForm, freeShippingThreshold: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div className="edit-zone-form-group">
                  <label>Estimated Delivery Days</label>
                  <Input
                    type="number"
                    value={zoneForm.estimatedDeliveryDays}
                    onChange={(e) => setZoneForm({...zoneForm, estimatedDeliveryDays: e.target.value})}
                    placeholder="3"
                  />
                </div>

                <div className="edit-zone-form-group">
                  <label>States</label>
                  <div className="edit-zone-states-container">
                    <div className="edit-zone-states-grid">
                      {allStates.map((state) => (
                        <label key={state} className="edit-zone-state-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedStates.includes(state)}
                            onChange={() => handleStateToggle(state)}
                          />
                          {state}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="edit-zone-form-group">
                  <label>Location overrides (towns, streets, postcodes)</label>
                  <div className="wizard-location-builder">
                    <div className="wizard-location-builder-row">
                      <Input
                        value={locationInput}
                        onChange={(e) => setLocationInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            syncLocationRatesFromInput();
                          }
                        }}
                        placeholder="Type one or more locations, then press Enter"
                      />
                      <button className="btn btn-add" onClick={syncLocationRatesFromInput} type="button">
                        Add
                      </button>
                    </div>
                    <div className="wizard-location-help">
                      Add a city or street here to give it a specific shipping price. The state price stays as the fallback.
                    </div>
                    {locationRates.length > 0 && (
                      <div className="wizard-location-rate-list">
                        {locationRates.map((item, index) => (
                          <div key={`${item.name}-${index}`} className="wizard-location-rate-row">
                            <Input
                              value={item.name}
                              onChange={(e) => updateLocationRate(index, 'name', e.target.value)}
                              placeholder="Location name"
                            />
                            <Input
                              type="number"
                              value={item.shippingCost}
                              onChange={(e) => updateLocationRate(index, 'shippingCost', e.target.value)}
                              placeholder="₦ cost"
                            />
                            <button className="btn btn-remove" onClick={() => removeLocationRate(index)} type="button">
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  );
};

export default ShippingSetupWizard;
